// client/src/components/pages/LexiStar/Maintenance/EventDetailDialog.tsx (ST-00249)
//
// One maintenance / breakdown / delay event: what, which truck, when, how long, notes, who booked
// and closed it — and what can still be done with it (lexistar.maintenance.manage):
//   booked     Start now · Change the expected end · Cancel
//   open       Close (now, or at an earlier time) · Extend · Cancel
//   closed     Cancel (it did not happen: never counted again, kept for the record)
//   cancelled  nothing
// Each action opens its own small form in place and posts to /maintenance/events/:id:<action>;
// a 409 (changed meanwhile, overlap) shows the server's words. Times are SAST.

import { useEffect, useState, type ReactNode } from 'react';
import { FaTimes } from 'react-icons/fa';
import { DateTimeField, inputClasses } from '../StatusBoard/atoms';
import { cancelEvent, closeEvent, errorText, extendEvent, startEvent } from '../../../../services/lexistarMaintenance';
import type { TimeEvent } from '../../../../types/lexistar-maintenance';
import { CATEGORY_META, STATE_TEXT, eventDuration, eventWhen, fmtDayTime, fmtDuration } from './maintenanceLogic';

export interface EventDetailDialogProps {
  event: TimeEvent;
  canManage: boolean;
  onClose: () => void;
  onChanged: (event: TimeEvent, message: string) => void;
}

type Action = 'start' | 'extend' | 'close' | 'cancel' | null;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-3 py-1.5 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 min-w-0 break-words">{children}</dd>
    </div>
  );
}

const btn = 'px-3 py-2 text-sm font-medium rounded-lg border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

export default function EventDetailDialog({ event, canManage, onClose, onChanged }: EventDetailDialogProps) {
  const meta = CATEGORY_META[event.category];
  const [action, setAction] = useState<Action>(null);
  const [when, setWhen] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Esc closes the dialog — unless it is closing an open date picker or dropdown first.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.react-datepicker-popper, .picker-sheet, .select__menu')) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const choose = (a: Action) => {
    setAction(a);
    setError(null);
    setNotes('');
    setReason('');
    setWhen(a === 'extend' && event.expected_end_at ? new Date(event.expected_end_at) : null);
  };

  const reg = event.registration || 'The truck';
  const run = async () => {
    if (busy || !action) return;
    setError(null);
    if (action === 'extend' && !when) return setError('Choose the new expected end');
    if (action === 'cancel' && reason.trim().length < 3) return setError('Say why it is cancelled');
    setBusy(true);
    try {
      const base = { version: event.version };
      let out: TimeEvent;
      let message: string;
      if (action === 'start') {
        out = await startEvent(event.id, { ...base, ...(when ? { at: when.toISOString() } : {}) });
        message = `${reg}: ${event.label} started.`;
      } else if (action === 'extend') {
        out = await extendEvent(event.id, { ...base, expected_end_at: (when as Date).toISOString(), notes: notes.trim() || null });
        message = `${reg}: ${event.label} now expected until ${fmtDayTime(out.expected_end_at)}.`;
      } else if (action === 'close') {
        out = await closeEvent(event.id, { ...base, ...(when ? { ends_at: when.toISOString() } : {}), notes: notes.trim() || null });
        message = `${reg}: ${event.label} closed after ${fmtDuration(eventDuration(out))}.`;
      } else {
        out = await cancelEvent(event.id, { ...base, reason: reason.trim() });
        message = `${reg}: ${event.label} cancelled.`;
      }
      onChanged(out, message);
      onClose();
    } catch (err) {
      setError(errorText(err, 'Could not save. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const actions: Array<{ id: Exclude<Action, null>; label: string; tone: string }> = [];
  if (canManage) {
    if (event.state === 'booked') actions.push({ id: 'start', label: 'Start now', tone: 'primary' });
    if (event.state === 'open') actions.push({ id: 'close', label: 'Close event', tone: 'primary' });
    if (event.state === 'open' || event.state === 'booked') actions.push({ id: 'extend', label: event.state === 'open' ? 'Extend' : 'Change end', tone: 'plain' });
    if (event.state !== 'cancelled') actions.push({ id: 'cancel', label: 'Cancel event', tone: 'danger' });
  }
  const dur = eventDuration(event);

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-6">
      <div role="dialog" aria-modal="true" aria-labelledby="maintenance-detail-title" className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-visible">
        <div className="flex items-start justify-between gap-4 px-6 pt-5">
          <div className="flex items-start gap-3 min-w-0">
            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${meta.pill}`} aria-hidden="true">
              <meta.Icon />
            </span>
            <div className="min-w-0">
              <h2 id="maintenance-detail-title" className="text-lg font-bold text-slate-900 truncate">
                {event.label}
              </h2>
              <p className="text-sm text-slate-600">
                <span className="font-mono font-semibold text-slate-800">{event.registration || '—'}</span> · {meta.label} ·{' '}
                <span className={event.overdue ? 'font-semibold text-amber-800' : ''}>{event.overdue ? 'Open, past its expected end' : STATE_TEXT[event.state]}</span>
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-slate-400 hover:text-slate-600">
            <FaTimes />
          </button>
        </div>

        <dl className="px-6 pt-3 pb-1 divide-y divide-slate-100">
          <Row label="When">{eventWhen(event)}</Row>
          <Row label={event.state === 'open' ? 'So far' : event.state === 'booked' ? 'Booked for' : 'Took'}>{dur === null ? '—' : fmtDuration(dur)}</Row>
          <Row label="Code">
            <span className="font-mono text-xs">{event.code}</span>
          </Row>
          {event.notes && <Row label="Notes">{event.notes}</Row>}
          <Row label="Booked">
            {fmtDayTime(event.booked_at)} by {event.booked_by}
          </Row>
          {event.closed_at && (
            <Row label="Closed">
              {fmtDayTime(event.closed_at)} by {event.closed_by}
            </Row>
          )}
          {event.cancelled_at && (
            <Row label="Cancelled">
              {fmtDayTime(event.cancelled_at)} by {event.cancelled_by}: {event.cancel_reason}
            </Row>
          )}
        </dl>

        {actions.length > 0 && (
          <div className="px-6 pt-3 pb-4 space-y-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="What to do with it">
              {actions.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => choose(action === a.id ? null : a.id)}
                  aria-pressed={action === a.id}
                  className={`${btn} ${
                    a.tone === 'primary'
                      ? 'border-primary-500 text-primary-700 bg-surface hover:bg-primary-50'
                      : a.tone === 'danger'
                        ? 'border-red-300 text-red-700 bg-surface hover:bg-red-50'
                        : 'border-slate-300 text-slate-700 bg-surface hover:bg-slate-50'
                  } ${action === a.id ? 'ring-2 ring-primary-400' : ''}`}
                >
                  {a.label}
                </button>
              ))}
            </div>

            {action && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3" data-action={action}>
                {(action === 'close' || action === 'start') && (
                  <div>
                    <label htmlFor="maintenance-action-when" className="block text-sm font-medium text-slate-700 mb-1">
                      {action === 'close' ? 'Ended' : 'Started'} <span className="font-normal text-slate-500">(empty = now)</span>
                    </label>
                    <DateTimeField id="maintenance-action-when" value={when} onChange={setWhen} placeholder="Now" maxDate={new Date()} />
                  </div>
                )}
                {action === 'extend' && (
                  <div>
                    <label htmlFor="maintenance-action-when" className="block text-sm font-medium text-slate-700 mb-1">
                      New expected end
                    </label>
                    <DateTimeField id="maintenance-action-when" value={when} onChange={setWhen} placeholder="yyyy/mm/dd hh:mm" />
                  </div>
                )}
                {(action === 'close' || action === 'extend') && (
                  <div>
                    <label htmlFor="maintenance-action-notes" className="block text-sm font-medium text-slate-700 mb-1">
                      Notes <span className="font-normal text-slate-500">(optional, replaces the notes)</span>
                    </label>
                    <textarea id="maintenance-action-notes" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className={`${inputClasses} resize-y`} />
                  </div>
                )}
                {action === 'cancel' && (
                  <div>
                    <label htmlFor="maintenance-action-reason" className="block text-sm font-medium text-slate-700 mb-1">
                      Why is it cancelled?
                    </label>
                    <input
                      id="maintenance-action-reason"
                      value={reason}
                      maxLength={500}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Booked by mistake, did not happen…"
                      className={inputClasses}
                    />
                    <p className="mt-1 text-xs text-slate-500">It stays on record, but is never counted again.</p>
                  </div>
                )}
                {error && (
                  <p role="alert" className="px-3 py-2 text-sm rounded-lg bg-red-50 border border-red-200 text-red-700">
                    {error}
                  </p>
                )}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={run}
                    disabled={busy}
                    className={`px-4 py-2 text-sm font-semibold text-white rounded-lg shadow-sm disabled:opacity-60 ${
                      action === 'cancel' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-500 hover:bg-primary-600'
                    }`}
                  >
                    {busy
                      ? 'Saving…'
                      : action === 'start'
                        ? 'Start it'
                        : action === 'close'
                          ? 'Close it'
                          : action === 'extend'
                            ? 'Save the new end'
                            : 'Cancel the event'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
